import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserCompanies } from "@/hooks/useUserCompanies";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileDown, Car, Wrench, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatBrazilianNumber } from "@/lib/utils";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface ProviderMaintenanceGroup {
  providerId: string;
  providerName: string;
  items: Array<{
    qty: number;
    description: string;
    unitPrice: number;
    totalPrice: number;
    workOrderNumber?: number;
    openedAt?: string;
  }>;
  subtotal: number;
}

interface WorkOrderWithItems {
  id: string;
  order_number: number;
  description: string;
  status: string;
  total_cost: number | null;
  opened_at: string;
  closed_at: string | null;
  provider_id: string | null;
  vehicle_id: string;
  fleet_providers: { id: string; name: string } | null;
  fleet_vehicles: { 
    id: string; 
    plate: string; 
    make: string; 
    model: string; 
    odometer: number | null;
    status: string;
  } | null;
  fleet_work_order_items: Array<{
    id: string;
    description: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }>;
}

function groupWorkOrdersByProvider(workOrders: WorkOrderWithItems[]): ProviderMaintenanceGroup[] {
  const groups = new Map<string, ProviderMaintenanceGroup>();
  
  for (const order of workOrders) {
    const providerId = order.provider_id || 'sem-fornecedor';
    const providerName = order.fleet_providers?.name || 'Sem Fornecedor';
    
    if (!groups.has(providerId)) {
      groups.set(providerId, { providerId, providerName, items: [], subtotal: 0 });
    }
    
    const group = groups.get(providerId)!;
    
    // Se tem itens detalhados, usar eles
    if (order.fleet_work_order_items?.length) {
      for (const item of order.fleet_work_order_items) {
        group.items.push({
          qty: item.quantity,
          description: item.description,
          unitPrice: item.unit_price,
          totalPrice: item.total_price,
          workOrderNumber: order.order_number,
          openedAt: order.opened_at
        });
        group.subtotal += item.total_price;
      }
    } else {
      // Fallback: usar descrição e total_cost da OS
      group.items.push({
        qty: 1,
        description: order.description || 'Serviço',
        unitPrice: order.total_cost || 0,
        totalPrice: order.total_cost || 0,
        workOrderNumber: order.order_number,
        openedAt: order.opened_at
      });
      group.subtotal += order.total_cost || 0;
    }
  }
  
  return Array.from(groups.values()).sort((a, b) => b.subtotal - a.subtotal);
}

export default function DetailedMaintenanceReport() {
  const { data: userCompanies } = useUserCompanies();
  const companyId = userCompanies?.[0]?.company_id;
  
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().split('T')[0]);

  // Buscar veículos
  const { data: vehicles } = useQuery({
    queryKey: ['fleet-vehicles', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('fleet_vehicles')
        .select('id, plate, make, model, odometer, status')
        .eq('company_id', companyId)
        .order('plate');
      if (error) throw error;
      return data;
    },
    enabled: !!companyId
  });

  // Buscar ordens de serviço com itens
  const { data: workOrders, isLoading } = useQuery({
    queryKey: ['work-orders-detailed', companyId, selectedVehicleId, startDate, endDate],
    queryFn: async () => {
      if (!companyId || !selectedVehicleId) return [];
      
      const { data, error } = await supabase
        .from('fleet_work_orders')
        .select(`
          id,
          order_number,
          description,
          status,
          total_cost,
          opened_at,
          closed_at,
          provider_id,
          vehicle_id,
          fleet_providers(id, name),
          fleet_vehicles(id, plate, make, model, odometer, status),
          fleet_work_order_items(id, description, quantity, unit_price, total_price)
        `)
        .eq('company_id', companyId)
        .eq('vehicle_id', selectedVehicleId)
        .gte('opened_at', `${startDate}T00:00:00`)
        .lte('opened_at', `${endDate}T23:59:59`)
        .order('opened_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as WorkOrderWithItems[];
    },
    enabled: !!companyId && !!selectedVehicleId
  });

  // Agrupar por fornecedor
  const providerGroups = useMemo(() => {
    if (!workOrders?.length) return [];
    return groupWorkOrdersByProvider(workOrders);
  }, [workOrders]);

  // Veículo selecionado
  const selectedVehicle = vehicles?.find(v => v.id === selectedVehicleId);

  // Totais
  const totalGeral = providerGroups.reduce((sum, g) => sum + g.subtotal, 0);
  const totalOS = workOrders?.length || 0;
  const totalItens = providerGroups.reduce((sum, g) => sum + g.items.length, 0);

  // Estatísticas
  const osAbertas = workOrders?.filter(o => o.status === 'open').length || 0;
  const osEmAndamento = workOrders?.filter(o => o.status === 'in_progress').length || 0;
  const osFechadas = workOrders?.filter(o => o.status === 'closed').length || 0;

  // Gerar PDF
  const handleGeneratePDF = () => {
    if (!selectedVehicle || !workOrders?.length) {
      toast.error('Selecione um veículo e verifique se há dados no período');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;

    // Título principal
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('RELATÓRIO DE MANUTENÇÃO DETALHADO', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 7;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Período: ${format(parseISO(startDate), 'dd/MM/yyyy')} a ${format(parseISO(endDate), 'dd/MM/yyyy')}`, pageWidth / 2, yPos, { align: 'center' });

    // Dados do veículo
    yPos += 10;
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPos, pageWidth - 20, 12, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    
    const vehicleInfo = [
      `MODELO: ${selectedVehicle.make} ${selectedVehicle.model}`,
      `PLACA: ${selectedVehicle.plate}`,
      `KM ATUAL: ${selectedVehicle.odometer?.toLocaleString('pt-BR') || 'N/A'}`,
      `STATUS: ${selectedVehicle.status === 'active' ? 'Ativo' : selectedVehicle.status === 'maintenance' ? 'Em Manutenção' : selectedVehicle.status}`
    ];
    
    const colWidth = (pageWidth - 20) / 4;
    vehicleInfo.forEach((info, idx) => {
      doc.text(info, 12 + (idx * colWidth), yPos + 7);
    });

    // Seção 1 - Status Operacional
    yPos += 18;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Status Operacional', 10, yPos);
    
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`• Total de Ordens de Serviço: ${totalOS}`, 15, yPos);
    yPos += 5;
    doc.text(`• Abertas: ${osAbertas} | Em Andamento: ${osEmAndamento} | Fechadas: ${osFechadas}`, 15, yPos);
    yPos += 5;
    doc.text(`• Total de Itens: ${totalItens}`, 15, yPos);

    // Seção 2 - Manutenções por Fornecedor
    yPos += 12;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('2. Manutenções Realizadas por Fornecedor', 10, yPos);
    yPos += 8;

    // Criar tabelas por fornecedor
    providerGroups.forEach((group, groupIdx) => {
      // Título do fornecedor
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(230, 230, 230);
      doc.rect(10, yPos - 4, pageWidth - 20, 7, 'F');
      doc.text(`${group.providerName}`, 12, yPos);
      doc.text(`Total: R$ ${formatBrazilianNumber(group.subtotal)}`, pageWidth - 50, yPos);
      yPos += 6;

      // Tabela de itens
      autoTable(doc, {
        startY: yPos,
        head: [['Qtd', 'Descrição', 'Valor Unit.', 'Valor Total']],
        body: group.items.map(item => [
          item.qty.toString(),
          item.description.length > 50 ? item.description.substring(0, 50) + '...' : item.description,
          `R$ ${formatBrazilianNumber(item.unitPrice)}`,
          `R$ ${formatBrazilianNumber(item.totalPrice)}`
        ]),
        foot: [['', 'SUBTOTAL', '', `R$ ${formatBrazilianNumber(group.subtotal)}`]],
        theme: 'striped',
        headStyles: { fillColor: [80, 80, 80], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        footStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontSize: 8, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 30, halign: 'right' }
        },
        margin: { left: 10, right: 10 }
      });

      yPos = (doc as any).lastAutoTable.finalY + 8;

      // Verificar se precisa de nova página
      if (yPos > 270 && groupIdx < providerGroups.length - 1) {
        doc.addPage();
        yPos = 15;
      }
    });

    // Total Geral
    yPos += 5;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(50, 50, 50);
    doc.setTextColor(255, 255, 255);
    doc.rect(10, yPos - 5, pageWidth - 20, 10, 'F');
    doc.text(`TOTAL GERAL: R$ ${formatBrazilianNumber(totalGeral)}`, pageWidth / 2, yPos + 2, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    // Rodapé
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm")} | Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Salvar
    const fileName = `manutencao-detalhado-${selectedVehicle.plate}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
    toast.success('PDF gerado com sucesso!');
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Relatório de Manutenção Detalhado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Veículo</Label>
              <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um veículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles?.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.plate} - {v.make} {v.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={handleGeneratePDF} 
                disabled={!selectedVehicleId || isLoading || !workOrders?.length}
                className="w-full"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Gerar PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {selectedVehicleId && selectedVehicle && (
        <>
          {/* KPIs */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Wrench className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total de OS</p>
                    <p className="text-2xl font-bold">{totalOS}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/10 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Em Andamento</p>
                    <p className="text-2xl font-bold">{osEmAndamento}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Fechadas</p>
                    <p className="text-2xl font-bold">{osFechadas}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Car className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Custo Total</p>
                    <p className="text-xl font-bold">R$ {formatBrazilianNumber(totalGeral)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dados do Veículo */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid gap-4 md:grid-cols-4 text-center">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">MODELO</p>
                  <p className="font-semibold">{selectedVehicle.make} {selectedVehicle.model}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">PLACA</p>
                  <p className="font-semibold">{selectedVehicle.plate}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">KM ATUAL</p>
                  <p className="font-semibold">{selectedVehicle.odometer?.toLocaleString('pt-BR') || 'N/A'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">STATUS</p>
                  <p className="font-semibold">
                    {selectedVehicle.status === 'active' ? 'Ativo' : 
                     selectedVehicle.status === 'maintenance' ? 'Em Manutenção' : 
                     selectedVehicle.status}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Manutenções por Fornecedor */}
          {isLoading ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Carregando dados...
              </CardContent>
            </Card>
          ) : providerGroups.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhuma ordem de serviço encontrada no período selecionado.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {providerGroups.map((group) => (
                <Card key={group.providerId}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>{group.providerName}</span>
                      <span className="text-primary">R$ {formatBrazilianNumber(group.subtotal)}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {group.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-start text-sm py-1 border-b border-muted last:border-0">
                          <div className="flex-1">
                            <span className="font-medium text-muted-foreground mr-2">{item.qty}x</span>
                            <span>{item.description}</span>
                          </div>
                          <span className="font-medium ml-2">R$ {formatBrazilianNumber(item.totalPrice)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Total Geral */}
          {providerGroups.length > 0 && (
            <Card className="bg-primary text-primary-foreground">
              <CardContent className="py-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">TOTAL GERAL DO PERÍODO</span>
                  <span className="text-2xl font-bold">R$ {formatBrazilianNumber(totalGeral)}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
